import React from "react";
import styles from "./CommunityEngagements.module.css";
import Image from "next/image";

import { extractDateAndTime } from "@/utils/utils";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { CalendarDays, Timer } from "lucide-react";
import Link from "next/link";

interface Image {
  id: number;
  documentId: string;
  name: string;
  alternativeText: string | null;
  url: string;
}

interface EventCard {
  id: number;
  title: string;
  date_time: string;
  location: string;
}
interface TypeLink {
  id: number;
  label: string;
  url: string;
}

interface FeatureEventCard {
  id: number;
  type: string | null;
  image: Image;
  event_card: EventCard;
  type_link: TypeLink;
}

interface UpcomingEventsData {
  id: number;
  title: string;
  feature_event_cards: FeatureEventCard[];
}

interface Props {
  data: UpcomingEventsData;
  isEvent?: boolean;
}

const UpcomingEvents: React.FC<Props> = ({ data, isEvent }) => {
  const { title, feature_event_cards } = data;

  return (
    <div
      className={`${
        isEvent
          ? styles.upcomingEventsContainerWhite
          : styles.upcomingEventsContainer
      } container`}
    >
      <div className={styles.upcomingEvents}>
        <h2 className={styles.CEheader}>{title}</h2>
        <div
          className={
            feature_event_cards?.length > 3
              ? styles.statCardContainer
              : styles.statCardGrid
          }
        >
          {feature_event_cards?.length > 3 ? (
            <div className={styles.swiperContainer}>
              <Swiper
                slidesPerView={4}
                spaceBetween={32}
                modules={[Pagination]}
                pagination={{
                  clickable: true,
                  type: "bullets",
                }}
                className={styles.swiperStyle}
                breakpoints={{
                  320: {
                    slidesPerView: 1,
                    spaceBetween: 24,
                  },
                  768: {
                    slidesPerView: 2,
                    spaceBetween: 24,
                  },
                  992: {
                    slidesPerView: 3,
                    spaceBetween: 32,
                  },
                  1200: {
                    slidesPerView: 3,
                    spaceBetween: 32,
                  },
                }}
              >
                {feature_event_cards?.map((event, index) => (
                  <SwiperSlide key={index} className={styles.swiperSlide}>
                    <StatCard
                      image={event?.image?.url}
                      date={event?.event_card?.date_time}
                      location={event?.event_card?.location}
                      description={event?.event_card?.title}
                      type_link={event?.type_link}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          ) : (
            feature_event_cards?.map((event, index) => (
              <StatCard
                key={index}
                image={event?.image?.url}
                date={event?.event_card?.date_time}
                location={event?.event_card?.location}
                description={event?.event_card?.title}
                type_link={event?.type_link}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default UpcomingEvents;

const StatCard = ({ image, date, location, description, type_link }: any) => {
  const { formateDate, formatTime } = extractDateAndTime(date);
  return (
    <div className={styles.statCard}>
      <Link key={type_link?.id} href={type_link?.url || ""} passHref>
        <Image
          src={image}
          alt="Event"
          title="Event"
          className={styles.statCardImage}
          width={200}
          height={250}
          priority
        />
        <div className={styles.statCardContent}>
          <div className={styles.dateAndTime}>
            <p className={styles.statCardDate}>
              <CalendarDays style={{ fontSize: "18px" }} />
              {formateDate}
            </p>
            <p className={styles.statCardDate}>
              <Timer style={{ fontSize: "18px" }} />
              {formatTime.toLowerCase()}
            </p>
          </div>

          <p className={styles.statCardDescription}>{description}</p>
          <p className={styles.statCardDate}>
            <Image
              src="/assets/location_white.svg"
              alt="Event"
              title="Event"
              width={20}
              height={20}
            />
            {location}
          </p>
        </div>
      </Link>
    </div>
  );
};
